/**
 * EForm 必填欄位驗證工具
 * 用於表單提交前驗證 required 屬性的欄位
 */

/**
 * 檢查金額欄位是否為空
 * "$0.00" 視為有填寫，僅 "" 或 "$" 視為空
 * @param {string} value - 輸入值
 * @returns {boolean} - true 表示空
 */
export const isCurrencyFieldEmpty = (value) => {
  if (value == null || typeof value !== 'string') return true;
  const stripped = value.replace(/[$,\s]/g, '').trim();
  return stripped === '';
};

/**
 * 檢查欄位是否為空（依類型）
 * @param {HTMLElement} el - 表單元素
 * @returns {boolean} - true 表示空
 */
const isFieldEmpty = (el) => {
  const tagName = (el.tagName || '').toLowerCase();
  const type = (el.type || '').toLowerCase();
  const inputType = el.getAttribute('data-input-type');

  if (tagName === 'input') {
    if (type === 'checkbox' || type === 'radio') {
      return !el.checked;
    }
    if (type === 'file') {
      return !el.files || el.files.length === 0;
    }
    if (inputType === 'currency') {
      return isCurrencyFieldEmpty(el.value);
    }
    // text, email, tel, url, number, date, time
    return !(el.value || '').trim();
  }

  if (tagName === 'textarea') {
    return !(el.value || '').trim();
  }

  if (tagName === 'select') {
    const idx = el.selectedIndex;
    const opt = el.options[idx];
    if (idx < 0 || !opt) return true;
    const val = (opt.value ?? '').toString().trim();
    return val === '';
  }

  return true;
};

/**
 * 取得 radio 群組中是否至少有一個被選中
 * @param {HTMLElement} container - 表單容器
 * @param {string} name - radio 的 name
 * @returns {boolean}
 */
const isRadioGroupFilled = (container, name) => {
  const radios = container.querySelectorAll(`input[type="radio"][name="${name}"]`);
  return Array.from(radios).some(r => r.checked);
};

/**
 * 取得欄位顯示標籤（用於錯誤訊息）
 * @param {HTMLElement} el
 * @returns {string}
 */
const getFieldLabel = (el) => {
  const id = el.id || el.name;
  if (id) {
    const label = document.querySelector(`label[for="${id}"]`);
    if (label) return (label.textContent || '').trim();
  }
  const placeholder = el.getAttribute('placeholder');
  if (placeholder) return placeholder;
  if (el.name) return el.name;
  if (el.id) return el.id;
  return '';
};

/**
 * 驗證表單必填欄位
 * @param {HTMLElement} container - 表單容器 (.form-content-inner)
 * @returns {{ valid: boolean, firstInvalidEl?: HTMLElement, fieldLabel?: string, messageType?: 'fill'|'upload'|'check'|'select' }}
 */
export const validateEFormRequiredFields = (container) => {
  if (!container || !container.querySelectorAll) {
    return { valid: true };
  }

  const processedRadioGroups = new Set();

  // 一般 input（含 text, email, tel, number, date, time, currency）與 textarea
  const textLikeInputs = container.querySelectorAll(
    'input[required]:not([type="checkbox"]):not([type="radio"]):not([type="file"]), textarea[required], select[required]'
  );
  for (const el of textLikeInputs) {
    if (isFieldEmpty(el)) {
      const label = getFieldLabel(el);
      return {
        valid: false,
        firstInvalidEl: el,
        fieldLabel: label,
        messageType: 'fill'
      };
    }
  }

  // file input
  const fileInputs = container.querySelectorAll('input[type="file"][required]');
  for (const el of fileInputs) {
    if (isFieldEmpty(el)) {
      const label = getFieldLabel(el);
      return {
        valid: false,
        firstInvalidEl: el,
        fieldLabel: label,
        messageType: 'upload'
      };
    }
  }

  // checkbox
  const checkboxes = container.querySelectorAll('input[type="checkbox"][required]');
  for (const el of checkboxes) {
    if (!el.checked) {
      const label = getFieldLabel(el);
      return {
        valid: false,
        firstInvalidEl: el,
        fieldLabel: label,
        messageType: 'check'
      };
    }
  }

  // radio（依 name 群組檢查）
  const radios = container.querySelectorAll('input[type="radio"][required]');
  for (const el of radios) {
    const name = el.name;
    if (!name || processedRadioGroups.has(name)) continue;
    processedRadioGroups.add(name);
    if (!isRadioGroupFilled(container, name)) {
      const label = getFieldLabel(el);
      return {
        valid: false,
        firstInvalidEl: el,
        fieldLabel: label,
        messageType: 'select'
      };
    }
  }

  return { valid: true };
};

export default validateEFormRequiredFields;
